import React, {
  useContext,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { PromiseState } from 'react-refetch';
import { compose } from 'recompose';
import { useSelector } from 'react-redux';
import styled from 'styled-components/macro';

import { stringify } from 'querystring';

import useMedia from 'hooks/useMedia';
import interceptConnect from 'helpers/interceptConnect';
import useComponentSettings from 'hooks/useComponentSettings';
import { EntityContext } from 'context/entityContext';
import useTrendData from '../hooks/useTrendData';
import useHasNoData from 'components/data-containers/PerformanceTrend/hooks/useHasNoData';
import useFilteredTrends from '../hooks/useFilteredTrends';
import getColorScale from '../helpers/getColorScale';
import existsIn from '../helpers/existsIn';
import getMargins from 'components/data-visualization/LineChart/helpers/getMargins';
import getTrendPoints from '../helpers/getTrendPoints';
import getFirstEntityDate from '../helpers/getFirstEntityDate';
import formatDate, { PeriodKey } from 'helpers/formatDate';
import { withGeneralSettings } from 'components/hocs/withGeneralSettings';

import { IAudienceTrendArgs } from 'helpers/interceptors/audienceTrendInterceptor';
import { IGeneralSettingsPropType, ISalesType } from '../types';
import getSchema from 'schemas/componentSettings/Audience/AudienceTrend';

import { selectCustomersByFeedType, ICustomer } from 'redux/reducers/customers';
import { TAsyncDispatchResult } from 'types/shared';
import { AvailablePeriodType } from 'components/data-containers/Audience/AgeOverTime/utils/shared';
import { audiencePeriodTypes } from 'maps/period-types';
import getDataTogglePairs from '../helpers/getDataTogglePairs';

import GenericComponent from 'components/utility/GenericComponent/GenericComponent';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import ComponentControls from 'components/styled/ComponentControls/ComponentControls';
import PeriodDropdown from 'components/utility/PeriodDropdown';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import Chart from 'components/data-visualization/Chart/Chart';
import Legend from '../shared/Legend';
import LineChart from 'components/data-visualization/LineChart/LineChart';
import LineChartSkeleton from 'components/data-visualization/LineChart/LineChartSkeleton/LineChartSkeleton';
import KeyValueLoader from 'components/utility/ContentLoader/KeyValueLoader/KeyValueLoader';
import DataToggleList, {
  DataListItem,
  DataList,
  FlexBox,
} from 'components/DataToggleList/DataToggleList';

import {
  StyledPerformanceTrendLoader,
  DataToggleContainer,
  Relative,
  VisiblePeriod,
  VisiblePeriodWrapper,
} from '../shared/PerformanceTrend.style';

const DataToggleAudienceContainer = styled(DataToggleContainer)<any>`
  @media screen and (min-width: 32.4375em) {
    ${DataList} {
      display: flex;
      flex-direction: row;
    }
    ${DataListItem} {
      display: flex;

      ${FlexBox} {
        flex-direction: column;
      }
    }
  }
`;

const margin = getMargins(true);

const dataSource = (): any => {
  return {
    trendDataFetch: ({
      entityIds,
      entityType,
      countryIds,
      customerFilter,
      period,
      compareMode,
    }: IAudienceTrendArgs): any => {
      const params = stringify({ period, countryIds, customerFilter });

      return {
        trendData: {
          url: `audienceTrend?${params}`,
          interceptionData: {
            entityIds,
            entityType,
            countryIds,
            customerFilter,
            period,
            compareMode,
          } as IAudienceTrendArgs,
        },
      };
    },
  };
};

interface IAudienceTrendProps {
  trendDataFetch: any;
  trendData: PromiseState<any>;
  generalSettings: IGeneralSettingsPropType;
  type?: 'Streams' | 'Listeners';
}

export const trendTypes = [
  {
    displayName: 'Streams',
    id: 'streams',
  },
  // {
  //   displayName: 'Listeners',
  //   id: 'listeners',
  // },
];

const AudienceTrend: React.FC<IAudienceTrendProps> = (props) => {
  const {
    trendDataFetch,
    trendData = PromiseState.create(),
    generalSettings,
    type,
  } = props;

  const [period, setPeriod] = useState<Date | null>(null);
  const [tempPeriod, setTempPeriod] = useState<Date | null>(null);
  const [selectedTrendPoints, setSelectedTrendPoints] = useState<any>();
  const [tempSelectedTrendPoints, setTempSelectedTrendPoints] = useState<any>();

  const trendTypesIds = useMemo(() => trendTypes.map((el) => el.id), []);
  /**
   * Media query to switch between mobile/desktop behavior
   */
  const isSmallScreen = useMedia('(max-width: 48em)');

  /**
   * Variables from context.
   */
  const ctx = useContext(EntityContext);
  const {
    entityIds,
    countryIds,
    entityType,
    filters,
    dataColors,
    compareMode,
  } = ctx;

  /**
   * Component State
   */
  const performanceTrendSchema = getSchema();
  let settingsId = 'audience-performance-trend';
  settingsId = type ? `${settingsId}-${type}` : settingsId;
  settingsId = compareMode ? `${settingsId}-compare` : settingsId;

  const [{ selectedInterval }, saveComponentSettings] = useComponentSettings<{
    selectedInterval: AvailablePeriodType;
  }>(settingsId, performanceTrendSchema);

  const selectedPeriod = audiencePeriodTypes[selectedInterval];

  const periodValue =
    selectedPeriod.value === 'twoWeeks' ? 'day' : selectedPeriod.value;

  const { fulfilled, pending, rejected, reason } = trendData;

  const customers = useSelector(selectCustomersByFeedType('streaming'));

  /**
   * Our custom colors for DSP customers, coming from the general settings.
   *
   * @return {Object} Map of DPS string id's to colors (streams and listeners from KV)
   */
  const customerColors = useMemo(() => {
    if (generalSettings.success) {
      return {
        streams: generalSettings.data.customerColors['streamingCustomers'],
        listeners:
          generalSettings.data.customerColors['streamingListenersCustomers'],
      };
    }
  }, [generalSettings]);

  const dataByType = useMemo(() => {
    if (type && trendData?.value?.length) {
      return trendData?.value?.filter(
        (data: any) => data.breakdown === type.toLowerCase()
      );
    } else {
      return trendData.value?.reason ? [] : trendData.value;
    }
  }, [type, trendData]);

  const availableTrends = useTrendData({
    trendData: dataByType,
    breakdown: trendTypesIds,
    withOutliers: false,
  });

  const croppedTrends = useMemo(() => {
    if (selectedPeriod.value === 'twoWeeks' && availableTrends) {
      return availableTrends.map((trend: any) => ({
        ...trend,
        data: trend.data.slice(0, 14),
      }));
    }
    return availableTrends;
  }, [availableTrends, selectedPeriod.value]);

  const hasNoData = useHasNoData(fulfilled, croppedTrends);

  const { filteredTrends, disabledTrends, toggleTrend } =
    useFilteredTrends(croppedTrends);

  useEffect(() => {
    trendDataFetch({
      entityIds,
      entityType,
      countryIds: compareMode === 'country' ? countryIds : filters?.country,
      period: periodValue,
      customerFilter: filters?.streaming,
      compareMode,
    });
  }, [
    filters,
    entityIds,
    entityType,
    trendDataFetch,
    periodValue,
    compareMode,
    countryIds,
  ]);

  /**
   * Event handler for period type updates
   *
   * @param  {Object} interval Contains a label/value for period types
   */
  const onPeriodTypeChange = (interval: {
    label: string;
    value: AvailablePeriodType;
  }): TAsyncDispatchResult =>
    saveComponentSettings({ selectedInterval: interval.value });

  const getCustomerColors = useCallback(
    (customerId?: number): string[] => {
      if (customerId) {
        const customer = customers.find((c: ICustomer) => c.id === customerId);
        if (customer) {
          const streamsColor =
            customerColors?.streams[customer.name] || customer.color;
          const listenersColor =
            customerColors?.listeners[customer.name] || `${customer.color}50`;

          return [streamsColor, listenersColor];
        }
      }
      return [];
    },
    [customers, customerColors]
  );

  const colorScale = useMemo(() => {
    return getColorScale(
      croppedTrends,
      compareMode
        ? dataColors
        : filters?.streaming
        ? getCustomerColors(filters?.streaming)
        : ['#007bea', '#ec3ab3'],
      undefined,
      undefined,
      compareMode
    );
  }, [croppedTrends, compareMode, filters, dataColors, getCustomerColors]);

  /**
   * Update the time period displayed when you hover in the graph. Only displayed
   * for devices without touch capabilities.
   *
   * @param  {Date} nextPeriod timestamp in ms
   */
  function onTempPeriodChange(nextPeriod: any): void {
    if (existsIn(croppedTrends, nextPeriod)) {
      setTempPeriod(nextPeriod);
    }
    // When a user moves the mouse out of the linechart hitbox, this is called with null.
    if (nextPeriod === null) {
      setTempPeriod(nextPeriod);
    }
  }

  /**
   * Set the initial period
   */
  useEffect(() => {
    if (!croppedTrends?.length) return;
    const initialPeriod = getFirstEntityDate(croppedTrends);
    setPeriod(initialPeriod.getTime());
  }, [croppedTrends]);

  /**
   * Update the time period displayed when you move the mouse out of the trend graph
   * Set this time period by clicking or tapping in the graph.
   *
   * @param  {Date} nextPeriod timestamp in ms
   */
  function onPeriodChange(nextPeriod: any): void {
    if (existsIn(croppedTrends, nextPeriod)) {
      setPeriod(nextPeriod);
    }
  }

  /**
   * Set the currently selected (by clicking) data points
   * @type {Array}
   */
  useEffect(() => {
    if (period) {
      setSelectedTrendPoints(getTrendPoints(filteredTrends, period));
    }
  }, [period, filteredTrends]);

  /**
   * Set the currently temporarily selected (by hovering) data points
   * @type {Array}
   */
  useEffect(() => {
    if (tempPeriod) {
      setTempSelectedTrendPoints(getTrendPoints(filteredTrends, tempPeriod));
    } else {
      setTempSelectedTrendPoints(null);
    }
  }, [filteredTrends, tempPeriod]);

  const isVisiblePeriodDisplayed =
    period && selectedPeriod && selectedPeriod.value;

  const dataToggleItems = useMemo(() => {
    if (tempPeriod || period) {
      return getDataTogglePairs({
        trends: croppedTrends,
        salesType: { label: 'Streaming', value: 'streaming' } as ISalesType,
        colorScale,
        customers,
        period: tempPeriod || period,
        useBreakdownLabel: true,
        useShortValue: true,
      });
    }
  }, [colorScale, period, tempPeriod, croppedTrends, customers]);

  const title = `Audience${type ? `, ${type}` : ''}`;
  const chartLabel = type || 'Streams';

  return (
    <GenericComponent
      title={title}
      glossarySlug="charts-graphs-tables/audience"
      allowedFilters={['country', 'streaming']}
      loading={pending}
      success={fulfilled}
      error={rejected}
      data-pw={title}
      isSmallScreen={isSmallScreen}
      isNarrowScreen
    >
      <ErrorBoundary error={reason}>
        <ComponentControls>
          <VisiblePeriodWrapper>
            {isVisiblePeriodDisplayed && (
              <VisiblePeriod
                label="Date"
                value={formatDate(
                  tempPeriod || period,
                  periodValue as PeriodKey,
                  true
                )}
                dataColor={'#000A29'}
              />
            )}
            {!isVisiblePeriodDisplayed && <KeyValueLoader />}
          </VisiblePeriodWrapper>
          <PeriodDropdown
            selectedItem={selectedPeriod}
            onChange={onPeriodTypeChange}
            customPeriods={audiencePeriodTypes}
            withoutDefault
            isRoundedDropdown="all"
          />
        </ComponentControls>

        <AnimatedLoadingWrapper loading={pending}>
          <DataToggleAudienceContainer>
            <DataToggleList
              data={dataToggleItems as any}
              expectedItemCount={2}
              loading={pending}
              handleClick={toggleTrend}
              toggledItems={disabledTrends}
              isSmallScreen={isSmallScreen}
              newDesign={true}
              withTooltip
              isStefDesign
              disableUncheckAll
            />
          </DataToggleAudienceContainer>
          <Relative>
            <Chart
              height={isSmallScreen ? 260 : 420}
              hasNoData={(fulfilled && !croppedTrends) || hasNoData}
              mediaQuery="(min-width: 40em)"
              isDimmed={pending}
              render={(renderProps): JSX.Element => {
                return (
                  <>
                    {selectedTrendPoints && (
                      <LineChart
                        showVerticalAxisLabels
                        fillFromLastNonNullValue
                        shouldTransitionIn={fulfilled}
                        period={periodValue}
                        colorScale={colorScale}
                        entities={filteredTrends}
                        trends={croppedTrends}
                        selectedEntities={selectedTrendPoints}
                        temporarySelectedEntities={tempSelectedTrendPoints}
                        handleMouseHover={onTempPeriodChange}
                        handleMouseClick={onPeriodChange}
                        hasNoData={(fulfilled && !croppedTrends) || hasNoData}
                        label={chartLabel}
                        isSmallScreen={isSmallScreen}
                        isStrictDesign
                        isLabelRotated
                        trackerLineStart
                        isCutGrid
                        {...renderProps}
                      />
                    )}
                    {!selectedTrendPoints && (
                      <LineChartSkeleton
                        width={renderProps.parentWidth}
                        height={
                          renderProps.parentHeight - margin.top - margin.bottom
                        }
                        marginTop={margin.top}
                      />
                    )}
                  </>
                );
              }}
            />

            {pending && !selectedTrendPoints && (
              <StyledPerformanceTrendLoader />
            )}
          </Relative>
        </AnimatedLoadingWrapper>

        <Legend />
      </ErrorBoundary>
    </GenericComponent>
  );
};

export default compose(
  withGeneralSettings,
  interceptConnect(dataSource as any)
)(AudienceTrend as any);
